import { useState } from 'react'
import type { Job } from '@workflow/contracts'
import { ActionMenu, AvatarStack, Button, Icons, Loading, StateNote, StatusLabel, type ActionMenuSection } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useArchiveJob, useAssignments, useDeleteJob, useDuplicateJob, useJob, useRestoreJob, useSession, useSetJobStatus } from '@/lib/api/hooks'
import { formatCreated, formatTimestamp } from '@/lib/format'
import { personName, playerOf } from '@/lib/people'
import { clientLabel } from '@/features/clients/clientStatus'
import { navigate } from '@/app/router'
import { Fact } from '@/shell/Fact'
import { PageFrame, Section } from '@/shell/PageFrame'
import { JobCrew } from './JobCrew'
import { JobDispatch } from './JobDispatch'
import { JobEditDialog } from './JobForm'
import { DraftBanner } from './JobSchedule'
import { JobNoteComposer, JobNotes } from './JobNotes'
import { JOB_STATUS, PRIORITY_LABEL, formatHours, formatMoney, statusMoves } from './jobsStatus'
import { JobStatusGlyph } from './JobStatusCircle'

const RATE_BASIS = { hourly: 'Per Hour', one_time: 'One-Time' } as const

/**
 * A pay term as decided: the client's rate is the studio's margin, spoken
 * only here; the player's rate carries its visibility word. Unset is a dash —
 * the record shows what was decided, nothing more.
 */
const describeRate = (job: Job, who: 'client' | 'player'): string => {
  const type = who === 'client' ? job.clientRateType : job.playerRateType
  const amount = who === 'client' ? job.clientRateAmount : job.playerRateAmount
  if (type === null || amount === null) return '—'
  const currency = job.currency ?? 'BDT'
  return `${formatMoney(amount)} ${currency} ${RATE_BASIS[type]}`
}

/**
 * A job's page: the record, editable in place; the crew on it, with what
 * each player is owed. Writing happens in the header — a quiet line under
 * the title, the way a page accepts a note — and the
 * thread gathers in its section. The pay terms read in the record and edit
 * with it; the ledger of what actually moved lives elsewhere until the
 * dedicated payment mechanism arrives.
 */
export function JobDetailPage({ id }: { id: string }) {
  const job = useJob(id)
  // The crew, for the stack beside the section's title — the same query the
  // crew list below runs, so the two can never disagree.
  const crew = useAssignments(id)
  // The row menu's Edit arrives with its question asked: `?edit=1` opens the
  // record already writable, one click saved.
  const [editing, setEditing] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('edit') === '1',
  )
  // Every hook stands above every return: the composer answers to the
  // header's menu as well as to its own line, and a state declared past the
  // loading return would move the hook count between renders and unmount the
  // whole page the moment the record arrives.
  const [noting, setNoting] = useState(false)

  if (job.isLoading) return <Loading label="Loading job" />
  if (job.error || !job.data) {
    return (
      <PageFrame title="Job">
        <StateNote
          title="This job could not be loaded."
          detail={describeError(job.error)}
          action={
            <Button size="sm" icon={<Icons.retry />} onClick={() => void job.refetch()}>
              Try again
            </Button>
          }
        />
      </PageFrame>
    )
  }

  const record = job.data
  const shown = JOB_STATUS[record.status]
  const openComposer = () => setNoting(true)
  // The people the job is live on — finished, dropped and removed hands are
  // history, not presence, so the stack shows only who is on it now.
  const onDeck = (crew.data ?? []).filter(
    (assignment) => assignment.completion === 'assigned' || assignment.completion === 'in_progress',
  )

  return (
    <PageFrame
      title={record.title}
      eyebrow={<span className="font-mono text-caption text-muted-foreground">{record.id} · Created {formatCreated(record.createdAt)}</span>}
      description={[clientLabel(record), record.typeName].join(' · ')}
      actions={
        <>
          <StatusLabel tone={shown.tone} hue={shown.hue} label={shown.label} />
          <Button size="sm" icon={<Icons.edit size="sm" />} disabled={record.archivedAt !== null} onClick={() => setEditing(true)}>
            Edit
          </Button>
          <JobActions job={record} onWriteNote={openComposer} />
        </>
      }
    >
      {record.status === 'draft' ? <DraftBanner job={record} /> : null}
      {record.archivedAt !== null ? <p className="mb-5 rounded-xl border bg-tint px-5 py-4 text-ui">This job is archived. Restore it to make changes.</p> : null}
      <JobEditDialog job={record} open={editing && record.archivedAt === null} onClose={() => setEditing(false)} />

      {/* Writing lives in the dialog the menu opens — one trigger, not two:
          the thread below gathers what was written, the page itself stays a
          record. */}
      <NoteDialog jobId={record.id} open={noting} onClose={() => setNoting(false)} />

      <Section title="The Record">
        <dl className="grid grid-cols-1 gap-y-0.5 text-ui sm:grid-cols-[10rem_1fr] sm:gap-x-4 sm:gap-y-2">
          <Fact label="Client" value={clientLabel(record)} />
          <Fact label="Category" value={record.typeName} />
          <Fact label="Workflow" value={record.workflowName} />
          <Fact label="Priority" value={PRIORITY_LABEL[record.priority]} />
          <Fact label="Work Description" value={record.description} pre />
          <Fact label="Started" value={record.actualStart ? formatTimestamp(record.actualStart) : 'Not Started Yet'} />
          {record.actualEnd ? (
            <Fact label={record.status === 'removed' ? 'Removed' : 'Completed'} value={formatTimestamp(record.actualEnd)} />
          ) : null}
          <Fact
            label="Players"
            value={playersLine(
              (crew.data ?? []).filter((assignment) => assignment.completion !== 'dropped' && assignment.completion !== 'removed').length,
              record.playersNeeded,
            )}
          />
          <Fact
            label="Hours"
            value={record.estimatedHours === null
              ? (record.loggedHours > 0 ? `${formatHours(record.loggedHours)} Logged` : null)
              : `${formatHours(record.loggedHours)} Logged Of ${formatHours(record.estimatedHours)}`}
          />
          <Fact label="Client Pays" value={record.clientRateType === null || record.clientRateAmount === null ? null : `${describeRate(record, 'client')} · Studio Only`} />
          <Fact
            label="Player Earns"
            value={
              record.playerRateType === null || record.playerRateAmount === null
                ? null
                : `${describeRate(record, 'player')} · ${record.playerRateVisible ? 'Visible To Players' : 'Hidden From Players'}`
            }
          />
          {record.clientName !== null ? (
            <Fact
              label="Client Name"
              value={`${record.clientName} · ${record.clientNameVisible ? 'Visible To Players' : 'Hidden From Players'}`}
            />
          ) : null}
        </dl>
      </Section>

      <Section
        title="Crew"
        aside={
          <AvatarStack
            people={onDeck.map((assignment) => ({
              key: assignment.id,
              name: personName(playerOf(assignment)),
              email: assignment.playerEmail,
            }))}
          />
        }
      >
        <JobCrew jobId={record.id} job={record} />
      </Section>

      <Section title="Dispatch">
        <JobDispatch jobId={record.id} job={record} />
      </Section>

      <Section title="Notes">
        <JobNotes entityType="job" entityId={record.id} />
      </Section>
    </PageFrame>
  )
}

/** The crew against what the work needs: "2 Of 5 Assigned", or just the count when the job never said. */
function playersLine(assigned: number, needed: number | null): string {
  return needed === null ? `${assigned} Assigned` : `${assigned} Of ${needed} Assigned`
}

/**
 * Everything the header does short of editing, behind one trigger: the
 * status moves the machine allows (worded as the decisions they are, from
 * the same transition map the API enforces), the record's acts, the archive
 * pair, and the one ending the machine does not have. Refusals — completing
 * with players still at work — arrive as the server's own toast, not inline.
 */
function JobActions({ job, onWriteNote }: { job: Job; onWriteNote: () => void }) {
  const move = useSetJobStatus()
  const duplicate = useDuplicateJob((copy) => navigate({ name: 'job', id: copy.id }))
  const archive = useArchiveJob()
  const restore = useRestoreJob()
  const remove = useDeleteJob(() => navigate({ name: 'home' }, { replace: true }))
  const isAdmin = useSession().data?.account?.role === 'admin'

  const targets = statusMoves(job, isAdmin)
  const busy = move.isPending || duplicate.isPending || archive.isPending || restore.isPending || remove.isPending
  const canArchive = job.archivedAt === null && (job.status === 'completed' || job.status === 'removed' || job.status === 'cancelled')

  const sections: ActionMenuSection[] = []
  if (targets.length > 0) {
    sections.push({
      title: 'Status',
      items: targets.map(({ target, label }) => ({
        key: target,
        label,
        icon: <JobStatusGlyph status={target} className="size-4" />,
        disabled: busy,
        onSelect: () => move.mutate({ id: job.id, status: target }),
      })),
    })
  }
  const recordItems: ActionMenuSection['items'] = [
    { key: 'note', label: 'Write a note', icon: <Icons.note size="sm" />, disabled: busy, onSelect: onWriteNote },
    { key: 'duplicate', label: 'Duplicate', icon: <Icons.copy size="sm" />, disabled: busy, onSelect: () => duplicate.mutate({ id: job.id }) },
  ]
  if (canArchive) {
    recordItems.push({ key: 'archive', label: 'Archive', icon: <Icons.archive size="sm" />, disabled: busy, onSelect: () => archive.mutate({ id: job.id }) })
  }
  if (job.archivedAt !== null) {
    recordItems.push({ key: 'restore', label: 'Restore', icon: <Icons.restore size="sm" />, disabled: busy, onSelect: () => restore.mutate({ id: job.id }) })
  }
  sections.push({ items: recordItems })
  sections.push({
    items: [{
      key: 'delete', label: 'Delete', icon: <Icons.trash size="sm" />, danger: true, disabled: busy,
      ask: { title: `Delete ${job.title}?`, detail: 'The job, its crew, notes and payments go with it. This cannot be undone.', confirmLabel: 'Delete' },
      onSelect: () => remove.mutate({ id: job.id, title: job.title }),
    }],
  })

  return <ActionMenu label={`Actions for ${job.title}`} trigger="labeled" sections={sections} />
}

/**
 * Writing, behind the menu's trigger: the shared writing window, opened from
 * Actions and closed by posting, cancelling, or leaving. The page keeps no
 * composer line of its own — the thread section below is the writing's
 * only other home.
 */
function NoteDialog({ jobId, open, onClose }: { jobId: string; open: boolean; onClose: () => void }) {
  return <JobNoteComposer entityType="job" entityId={jobId} hideTrigger open={open} onOpenChange={(next) => { if (!next) onClose() }} />
}
